// External dependencies
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import { _isEmpty, _isNotEmpty } from '@/littledash';

// Model types
import type { Animal } from '@/model/Animal.model';
import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { Study } from '@/model/Study.model';

// Support modules
import { errorToast, successToast } from '@/helpers';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import useGroupedDraggable from '@/support/Hooks/draggable/useGroupedDraggable';
import { filterDataByFilters } from '@/components/UI/Table/Table.utils';
import { sortAscByProperty } from '@/utils/array';
import { modalAction } from '@/utils/modal';

// Internal components
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import { findDuplicateNames } from '@/components/Cages/Show/Show.utils';
import { cageNameValidater } from '@/components/Colony/Colony.utils';

// Relative imports
import AssignCages from './AssignCages';
import CageColumns from './CageColumns';
import CageFilter from './CageFilter';
import { assembleCageFilterOptions } from './CageFilters';

export interface CageCardCollection extends Omit<Cage, 'subjects'> {
  subjects?: { data: Array<Animal> };
  duplicateCageName?: boolean;
}

interface CagesProps {
  cages: Array<Cage>;
  study: Study;
  fetchCages: () => void;
  inStudyView: boolean;
}

interface TableFilterOption {
  value: string;
  name: string;
  type?: string;
  operations: Array<{
    name: string;
    value: string;
    options?: Array<{ name: string; value: string | number }>;
  }>;
}

interface TableFilter {
  category: { index: number };
  operation: { index: number };
  option: { value: string | number };
}

const Cages = ({ cages = [], study, fetchCages, inStudyView }: CagesProps) => {
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);
  const [collections, setCollections] = useState<Array<CageCardCollection>>([]);
  const [filters, setFilters] = useState<Array<TableFilter>>([]);
  const [filterMatch, setFilterMatch] = useState(true);
  const [filterOptions, setFilterOptions] = useState<Array<TableFilterOption>>([]);
  const [animalIdProperty, setAnimalIdProperty] = useState<ID>('name');
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);

  const {
    response: animalsResponse,
    invoke: fetchAnimals,
    loading: animalsLoading,
  } = useApiHook({
    endpoint: inStudyView ? 'GET /api/v1/studies/{studyId}/animals' : 'GET /api/v1/animals',
    ...(study?.api_id && { path: { studyId: study.api_id } }),
    invokeOnInit: true,
    query: {
      perPage: -1,
      ...(_isNotEmpty(cages)
        ? {
            filterType: 'and',
            'filters[]': [`cage|in|${cages.map(({ id }) => id).join(',')}`, 'cage|ne|null'],
          }
        : {}),
    },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const animals = useMemo((): Array<Animal> => {
    if (animalsResponse?.type === 'success' && Array.isArray(animalsResponse.body?.data)) {
      return animalsResponse.body.data as unknown as Array<Animal>;
    }
    return [];
  }, [animalsResponse]);

  const initializeState = () => {
    const oldCages = [...collections];
    const initialCollections = findDuplicateNames(
      cages.map((collection) => {
        const visible = oldCages.find((cage) => cage.id === collection.id)?.isVisible;
        const subjects = animals.filter((animal) => animal.cage_id === collection.id);
        return {
          ...collection,
          isVisible: visible ?? true,
          subjects: {
            data: subjects.sort(sortAscByProperty('number')),
          },
        };
      })
    );
    setCollections(initialCollections);
    const options = assembleCageFilterOptions(study?.study_groups || []);
    setFilterOptions(options);
  };

  useEffect(() => {
    initializeState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animals]);

  useEffect(() => {
    fetchAnimals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cages]);

  const updateFilters = (newFilters: Array<TableFilter>, newFilterMatch: boolean) => {
    setFilters(newFilters);
    setFilterMatch(newFilterMatch);
  };

  const validateCageName = (name: string): boolean => {
    return !(collections.some((c) => c.name === name) ?? false) && cageNameValidater(name);
  };

  const updateCageAPI = (cage: CageCardCollection) => {
    Http.put(apiRoute('cages.update', { id: cage.id }), {
      name: cage.name,
    }).catch(() => {
      errorToast('There was a problem renaming the cage');
    });
  };

  const handleCageNameChange = (cage: CageCardCollection) => {
    setCollections((prevCages) => {
      const updatedCages = prevCages.map((prevCage) =>
        prevCage.id === cage.id
          ? {
              ...prevCage,
              name: cage.name,
            }
          : prevCage
      );
      const duplicatedCageNames = findDuplicateNames(updatedCages);
      if (showDuplicatesOnly && duplicatedCageNames.every((c) => !(c.duplicateCageName ?? false))) {
        setShowDuplicatesOnly(false);
      }
      return duplicatedCageNames;
    });
  };

  const updateCageState = (cage: CageCardCollection) => {
    const updatedCages = [...collections];
    const index = updatedCages.findIndex((c) => c.id === cage.id);
    updatedCages[index] = cage;
    setCollections(updatedCages);
  };

  const removeCageFromState = (id: ID) => {
    const updatedCages = [...collections];
    const index = updatedCages.findIndex((c) => c.id === id);
    updatedCages.splice(index, 1);
    setCollections(updatedCages);
  };

  const deleteCage = async (collection: CageCardCollection) => {
    try {
      await Http.delete(apiRoute('cages.destroy', { id: collection.id }));
      removeCageFromState(collection.id);
      successToast('Successfully deleted cage');
    } catch (error) {
      setDeleteError(error as Error);
    }
  };

  const toggleDeleteCageModal = (collection: CageCardCollection) => {
    openModal('CONFIRM_DELETE_COLLECTION', {
      closeModal,
      onClick: () => deleteCage(collection),
    });
  };

  const handleAddAnimalsCallback = (response: { data: { data: Array<Animal> } }, cage: CageCardCollection) => {
    const {
      data: { data: animalsCreated },
    } = response;
    const updatedAnimalsInCage: CageCardCollection = {
      ...cage,
      subjects: { data: [...(cage.subjects?.data ?? []), ...animalsCreated] },
    };
    updateCageState(updatedAnimalsInCage);
    closeModal();
  };

  const toggleAddAnimalsModal = (cage: CageCardCollection) => {
    openModal('ADD_SUBJECTS', {
      cage,
      handleCallback: handleAddAnimalsCallback,
    });
  };

  const filterSubjects = useCallback(
    (subjects: Array<Animal>) => {
      return filterDataByFilters(subjects, filters, filterMatch, filterOptions);
    },
    [filters, filterMatch, filterOptions]
  );

  const cagesWithFilteredSubjects = useMemo(() => {
    return collections.reduce<Array<CageCardCollection>>((acc, cage) => {
      if (!(cage.isVisible ?? false)) {
        return acc;
      }

      const filteredSubjects = filterSubjects(cage.subjects?.data ?? []);
      if (_isEmpty(filteredSubjects) && _isNotEmpty(filters)) {
        return acc;
      }

      const updatedCage: CageCardCollection = {
        ...cage,
        subjects: {
          ...cage.subjects,
          data: filteredSubjects,
        },
      };

      if (showDuplicatesOnly && !(updatedCage.duplicateCageName ?? false)) {
        return acc;
      }
      acc.push(updatedCage);
      return acc;
    }, []);
  }, [collections, filters, filterMatch, filterOptions, showDuplicatesOnly, filterSubjects]);

  const { addItemCallback } = useGroupedDraggable(cagesWithFilteredSubjects, setCollections);

  if (animalsLoading) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }

  const cagesEmpty = _isEmpty(collections);

  return animalsResponse?.type === 'error' ? (
    <ApiErrorBanner className="ml3" errorType={ApiErrorType.FETCH} error={animalsResponse?.error} />
  ) : (
    <div className={cn({ 'bg-white': !cagesEmpty }, 'h-100 overflow-auto')}>
      {deleteError && <ApiErrorBanner className="ml3" errorType={ApiErrorType.FETCH} error={deleteError} />}
      <div className="pb5">
        {cagesEmpty ? (
          <AssignCages study={study} setCages={setCollections} fetchCages={fetchCages} />
        ) : (
          <>
            <CageFilter
              study={study}
              cages={collections}
              fetchCages={fetchCages}
              setCages={setCollections}
              filters={filters}
              filterMatch={filterMatch}
              filterOptions={filterOptions}
              updateFilters={updateFilters}
              animalIdProperty={animalIdProperty}
              setAnimalIdProperty={setAnimalIdProperty}
              inStudyView={inStudyView}
              showDuplicatesOnly={showDuplicatesOnly}
              setShowDuplicatesOnly={setShowDuplicatesOnly}
            />
            <CageColumns
              study={study}
              cages={cagesWithFilteredSubjects}
              toggleAddAnimalsModal={toggleAddAnimalsModal}
              toggleDeleteCageModal={toggleDeleteCageModal}
              onAddMove={addItemCallback}
              updateCageAPI={updateCageAPI}
              validateCageName={validateCageName}
              onCageNameChange={handleCageNameChange}
              animalIdProperty={animalIdProperty}
              fetchCages={fetchCages}
              inStudyView={inStudyView}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Cages;
